<template>
  <form v-show="inited" class="field-form" @submit.prevent="$emit('submit')">
    <div
      class="form-group field-form__name"
      :class="{
        'is-invalid': isSubmitted && nameError,
      }"
    >
      <c-input
        v-model="form.data.name"
        floating-label="Наименование поля"
        :label-full-name="
          isCopy && 'При изменении названия поля, оно будет обновлено во всех анкетах, в которых используется'
        "
        :max-length="255"
        :clearable="true"
        :required-mark="true"
        :required="isSubmitted && nameError"
        :required-message="isSubmitted && nameError"
      />
    </div>

    <div class="row">
      <div class="col col-12 col-lg-8">
        <div class="form-group field-form__type">
          <c-input
            v-model="form.data.fieldType"
            type="select"
            label="Тип поля"
            :label-full-name="
              (isCopy || blocked) &&
              `Значение параметра «Тип поля» нельзя изменить, потому что выбранное поле используется в других анкетах`
            "
            :searchable="true"
            :items="fieldTypes"
            :disabled="isCopy || blocked"
          />
          <div v-if="isFile" class="form-note">Допустимые форматы: jpeg, jpg, png, gif, pdf, docx</div>
          <div v-if="blocked" class="form-note">
            Параметр уже используется в другой анкете, поэтому изменить его нельзя
          </div>
        </div>
      </div>
      <div v-if="hasSize" class="col col-12 col-lg-4">
        <div class="form-group field-form__size">
          <c-input v-model="form.data.size" type="select" label="Размер поля" :items="fieldSizes" />
        </div>
      </div>
    </div>

    <div class="form-group checkbox">
      <c-checkbox v-model="form.data.mobile" type="checkbox" label="Показывать в мобильном приложении" />
    </div>

    <div class="form-group checkbox">
      <c-checkbox v-model="form.data.available" type="checkbox" label="Доступно сотруднику для редактирования" />
    </div>

    <div v-if="canBeRequired" class="form-group checkbox">
      <c-checkbox v-model="form.data.required" type="checkbox" label="Обязательное" />
    </div>

    <div v-if="isAvailableForContract" class="form-group checkbox">
      <c-checkbox v-model="form.data.contract" type="checkbox" label="Доступно для договора" />
    </div>

    <div v-if="isFile">
      <div class="form-group checkbox">
        <c-checkbox
          v-model="form.data.limitFiles"
          type="checkbox"
          label="Ограничение по количеству загруженных файлов"
        />
      </div>

      <transition name="slide">
        <div
          v-if="form.data.limitFiles"
          class="form-group field-form__file-count"
          :class="{
            'is-invalid': isSubmitted && filesCountError,
          }"
        >
          <c-input
            v-model="form.data.maxFiles"
            class="files-count"
            label="Max кол-во"
            label-full-name="Ограничение на количество загружаемых файлов"
            type="integer-number"
            placeholder="0"
            :required="isSubmitted && filesCountError"
            :required-condition="isSubmitted && filesCountError"
          />
        </div>
      </transition>
    </div>

    <div v-if="isText">
      <div class="form-group checkbox">
        <c-checkbox v-model="form.data.multiline" type="checkbox" label="Многострочное" />
      </div>

      <div class="form-group checkbox">
        <c-checkbox v-model="form.data.limitText" type="checkbox" label="Ограничение по количеству знаков" />
      </div>

      <transition name="slide">
        <div
          v-if="form.data.limitText"
          class="form-group field-form__text-length"
          :class="{
            'is-invalid': isSubmitted && textLengthError,
          }"
        >
          <div class="c-input-label">
            Кол-во знаков, от — до
            <info-button
              icon="info"
              tooltip="Для поля ввода можно указать минимальное и максимальное количество символов, которые можно ввести при заполнении параметра. Возможно указать только одну из значений диапазона."
              size="small"
              no-border
            />
          </div>
          <div class="text-length">
            <c-input
              v-model="form.data.minLength"
              type="integer-number"
              class="from"
              placeholder="0"
              :required="isSubmitted && textLengthError"
            />
            <span class="divider">–</span>
            <c-input
              v-model="form.data.maxLength"
              type="integer-number"
              class="to"
              placeholder="0"
              :required="isSubmitted && textLengthError"
            />
          </div>

          <error-feedback v-if="isSubmitted && textLengthError" :text="textLengthError"></error-feedback>
        </div>
      </transition>
    </div>

    <list-params
      v-if="isList"
      :form="form.data"
      :field-name="form.data.name"
      :field-type="currentFieldType"
      @change="inited && $emit('changeDictionary', currentFieldType)"
    />

    <template v-if="isVariants">
      <field-variants
        v-model="form.data.variants"
        :error="isSubmitted && variantsError"
        class="field-variants"
        @change="inited && $emit('changeDictionary', currentFieldType)"
      />
    </template>

    <div v-if="warning">
      <error-feedback class="warning" :text="warningText" />
    </div>
  </form>
</template>

<script>
import CInput from "@/components/controls2/Input";
import InfoButton from "@/components/controls/button";
import CCheckbox from "@/components/controls/switcher";
import ErrorFeedback from "@/components/error-feedback";

import FieldVariants from "./FieldVariants";
import ListParams from "./ListParams";

import { REQUIRED_ERROR, NUMBER_RANGE_ERROR } from "@/constants/errorFieldValues";
import {
  CUSTOM_TYPES,
  CUSTOM_TYPES_LIST,
  FIELD_TYPE_LIST_SINGLE,
  FIELD_TYPE_LIST_MULTIPLE,
  FIELD_TYPE_FILE,
  FIELD_TYPE_RADIO,
  FIELD_TYPE_CHECKBOXES,
  FIELD_TYPE_TEXT,
  FIELD_TYPE_DATE,
  FIELD_TYPE_PERIOD,
} from "@/domains/profile/constants/custom-types";
import { FIELD_SIZES } from "@/domains/profile/constants/field-sizes";

export default {
  components: {
    CInput,
    InfoButton,
    ErrorFeedback,
    CCheckbox,
    FieldVariants,
    ListParams,
  },
  props: ["form", "inited", "isCopy", "warning", "blocked", "isSubmitted"],
  data() {
    return {
      fieldTypes: CUSTOM_TYPES_LIST,
      fieldSizes: FIELD_SIZES,
    };
  },
  computed: {
    currentFieldType() {
      const type = this.form.data.fieldType;
      return type.value;
    },

    isList() {
      return CUSTOM_TYPES[this.currentFieldType].isList;
    },

    isVariants() {
      return CUSTOM_TYPES[this.currentFieldType].isVariants;
    },

    hasSize() {
      if (this.currentFieldType === FIELD_TYPE_FILE) return false;
      if (this.currentFieldType === FIELD_TYPE_DATE) return false;
      if (this.currentFieldType === FIELD_TYPE_PERIOD) return false;
      if (this.isVariants) return false;
      return true;
    },

    isText() {
      return this.currentFieldType === FIELD_TYPE_TEXT;
    },

    isFile() {
      return this.currentFieldType === FIELD_TYPE_FILE;
    },

    isAvailableForContract() {
      return !this.isFile;
    },

    canBeRequired() {
      const type = CUSTOM_TYPES[this.currentFieldType];
      return !type.optional && !type.isBoolean;
    },

    nameError() {
      return this.form.data.name.trim() ? null : REQUIRED_ERROR;
    },

    filesCountError() {
      if (this.currentFieldType !== FIELD_TYPE_FILE) return null;
      if (!this.form.data.limitFiles) return null;
      if (!this.form.data.maxFiles) return REQUIRED_ERROR;
      const count = parseInt(this.form.data.maxFiles, 10);
      if (count === 0) return "Значение должно быть больше 0";
      return null;
    },

    textLengthError() {
      if (this.currentFieldType !== FIELD_TYPE_TEXT) return null;
      if (!this.form.data.limitText) return null;
      const { minLength, maxLength } = this.form.data;

      if (!minLength && !maxLength) return REQUIRED_ERROR;

      if (minLength && maxLength && +minLength > +maxLength) return NUMBER_RANGE_ERROR;
      return null;
    },

    variantsError() {
      if (!this.isVariants) return null;

      const hasVariants = this.form.data.variants.some((v) => v.text.trim());

      return hasVariants ? null : "Должен быть введён хотя бы один вариант";
    },

    isValid() {
      const isValid = !this.nameError && !this.filesCountError && !this.textLengthError && !this.variantsError;
      this.$emit("valid", isValid);
      return isValid;
    },

    warningText() {
      if (this.isList)
        return "При сохранении настроек название поля и значения в справочнике изменятся для всех анкет, в которых используется выбранное поле. Остальные параметры поля изменятся только для текущего типа анкеты.";
      if (this.isVariants)
        return "При сохранении настроек название поля и варианты ответов изменятся для всех анкет, в которых используется выбранное поле. Остальные параметры поля изменятся только для текущего типа анкеты.";
      return "При сохранении настроек название поля изменится для всех анкет, в которых используется выбранное поле. Остальные параметры поля изменятся только для текущего типа анкеты.";
    },
  },
  watch: {
    "form.data.fieldType": function (newValue, oldValue) {
      if (!this.inited) return;

      let needReset = true;

      const saveValuesTypes = {
        [FIELD_TYPE_RADIO]: FIELD_TYPE_CHECKBOXES,
        [FIELD_TYPE_CHECKBOXES]: FIELD_TYPE_RADIO,
        [FIELD_TYPE_LIST_SINGLE]: FIELD_TYPE_LIST_MULTIPLE,
        [FIELD_TYPE_LIST_MULTIPLE]: FIELD_TYPE_LIST_SINGLE,
      };

      if (newValue && oldValue) {
        const newType = newValue.value;
        const oldType = oldValue.value;

        if (saveValuesTypes[newType] === oldType) needReset = false;
      }

      if (needReset) {
        this.form.resetAdditionalValues();
      }
    },
  },
  created() {},
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars.scss";
.field-form {
  .form-group.checkbox {
    margin-bottom: 20px;
  }

  .field-variants {
    margin-top: 40px;
  }

  &__type,
  &__size {
    /deep/ {
      .c-input-container-list-item-label {
        white-space: normal;
        line-height: 1.2;
        padding: 3px 0;
      }
    }
  }
  &__text-length {
    /deep/ {
      .c-button__icon {
        transform: translateY(5px);
      }
    }
  }
  .text-length {
    display: flex;

    .divider {
      margin: 0 10px;
    }

    .from,
    .to {
      width: 70px;
    }
  }
  .files-count {
    width: 70px;

    /deep/ {
      .c-input-label {
        white-space: nowrap;
      }
    }
  }
  .is-invalid {
    padding-bottom: 25px;
  }

  @include beforeBootstrapMd {
    /deep/ .c-switcher-group-item {
      height: auto;
      &-label {
        white-space: normal;
        text-align: center;
      }
    }
  }
}
.warning {
  align-items: flex-start;
  margin-top: 15px;

  /deep/ {
    .error-feedback__text {
      color: black;
    }
  }
}
</style>
